/**
 * THE TWO LEVELS OF THE BUILDER — pure, no React.
 *
 * A LEVEL IS AN INPUT BURDEN. It answers "how much does the operator have to
 * supply?" — not "how are the pixels made".
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠️ THE LEVELS SHIPPED IN v2.38.0 WERE WRONG, AND THE MEASUREMENT SAYS SO
 *
 * Brian, 2026-09-09: *"it seems there truly isnt a difference in sourced build
 * and generated concepts... A generated concept I thought was supposed to be
 * more of a preloaded heres what we want — a championship ring for Duke
 * Basketball 2027 ACC Champions — without any real inputs or logos and it
 * sources all the data we need."*
 *
 * He was right. The previous version of this file mapped a level 1:1 onto the
 * engine's `mode: render | generate`, and MEASURED against the shipped
 * assembler (`sr-portal-backend/src/artwork/promptAssembler.js`, 2026-09-09)
 * that flag moves almost nothing:
 *
 *   assembleBuild(sameBuild, mode:'render')    →  5,319 chars
 *   assembleBuild(sameBuild, mode:'generate')  →  5,347 chars
 *   sections differing: 2 of 18
 *     · the 400-char MODE paragraph (`modeClause`)
 *     · the word `render`/`generate` echoed in the COMPONENTS header line
 *   beast, slots, components, LOCKED LAYER and output: BYTE-IDENTICAL
 *
 * And on this side, `validateStudio()` never read `mode` at all: both levels
 * demanded the same 13 steps, the same picked bezel and shanks, the same
 * hand-chosen crest. Two levels that differ by one paragraph out of eighteen
 * are one level with a spare label, which is exactly what a rep was seeing.
 *
 * Worse, the generate clause CONTRADICTED the build it was attached to: it
 * says *"where a real component does not exist yet"* while the COMPONENTS block
 * two sections down still said `BEZEL/TOP = base the selected SR bezel & top
 * design`. The operator had picked one, because the level made them.
 *
 * ⚠️ THE RIGHT AXIS WAS ALREADY WRITTEN DOWN, IN THE GPT. The Custom GPT's
 * instructions (`ai-tools/ring-concept-gpt/INSTRUCTIONS.md` §5, and §12's
 * response format) carry TWO axes and report them on two separate lines:
 *
 *   OPERATING MODE   Exploratory Concept · Directed Design   ← who supplies what
 *   BUILD MODE       render · generate                       ← how pixels are made
 *
 * §5, verbatim: *"Exploratory Concept Mode — SR wants a proposed direction from
 * verified context and approved precedent"* versus *"Directed Design Mode — the
 * user supplies components, references, wording, substitutions or structure."*
 *
 * We shipped the SECOND axis under the FIRST one's name. Brian's "sourced" is
 * Directed Design; his "generated concept" is Exploratory Concept. `mode` stays
 * the wire value it always was — it is still posted, still real — but it is no
 * longer what a level MEANS.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * BOTH LEVELS ARE BUILT TO THE SAME SPEC. Same locked layer, same six SR
 * metals, same stone vocabulary, same `buildPayload()` contract, same
 * `validateStudio()` gate, same status ladder. `SHARED_GUARANTEES` is that
 * promise, rendered once and asserted, so a future level cannot quietly ship
 * with a thinner one. A level changes the INPUT PATH and nothing that is
 * protected.
 */

export const LEVEL_SOURCED = 'sourced'
export const LEVEL_GENERATED = 'generated'
export const LEVEL_BEZEL_DROP = 'bezeldrop'

/**
 * ⚠️ LEVEL 3 IS NOT A RING. Brian, 2026-09-18: *"it must have the third working
 * level"* — he went looking for Bezel Drop in this control and it was not there.
 *
 * A Bezel Drop is ONE client mark held across a LIBRARY of existing ring
 * housings, output as a single branded sheet: landscape, pure black field, soft
 * reflections under each piece, roughly nineteen housings in three rows, all
 * straight on to the camera, the SR hexagon and line top left, the Southern
 * Recognition Inc watermark faded behind. It is a sales sheet, not a build.
 *
 * So it does NOT walk the thirteen steps, it does NOT post to the render
 * engine, and its `mode` is never a wire value — `viaBridge: true` says the
 * pixels come back through the GPT bridge (the same proven path the ring views
 * use) rather than from `POST /api/artwork/render`. That is why `levelRows()`
 * does not check it against the engine's `modes` list: an engine that has never
 * heard of it is not evidence that it is unavailable.
 */

/**
 * What is true of EVERY level. If a level ever needs to weaken one of these,
 * it is not a level — it is a different product, and it needs its own review.
 */
export const SHARED_GUARANTEES = [
  'The SR maker’s mark is on every build, preliminary included.',
  'The client crest is the real owned file, reproduced faithfully — never redrawn.',
  'Titles, names, records and signatures are exact keyed type. No model produces those pixels.',
  'The six SR metals, one stone vocabulary, one payload contract.',
  'Every render lands as a draft. Nothing auto-publishes.',
]

export const LEVELS = [
  {
    id: LEVEL_SOURCED,
    n: 1,
    mode: 'render',
    /* The GPT's own name for this axis position. Carried so the two systems
     * describe the same build with the same word. */
    operatingMode: 'Directed Design',
    label: 'Sourced build',
    tagline: 'You supply everything. Nothing is inferred.',
    /* THE FIELD THAT MAKES A LEVEL REAL. `steps: true` draws the 13-step
     * spine; `steps: false` draws the brief. This is a structural difference in
     * what is on screen, not a flag in a payload. */
    steps: true,
    inputBurden: 'Thirteen steps — every component, the crest, the keyed type, the materials.',
    what: 'The piece is ASSEMBLED from things that exist: a real housing, the bezel and shanks you picked out of the Canva library, the owned crest from the brand kit, the stones and metal you chose. Nothing about the geometry is imagined, and nothing about the job is inferred.',
    guarantees: [
      'Every visual element is a real, selected input — nothing is invented.',
      'You decide each of the thirteen answers; the engine decides none of them.',
      'Reproducible: the build snapshot is written before the render, so the same record renders the same piece.',
    ],
  },
  {
    id: LEVEL_GENERATED,
    n: 2,
    mode: 'generate',
    operatingMode: 'Exploratory Concept',
    label: 'Generated concept',
    tagline: 'One line. The system sources the rest.',
    steps: false,
    inputBurden: 'One line — "a championship ring for Duke Basketball 2027 ACC Champions".',
    what: 'Describe the job in a sentence. The system matches it to the championship record and the school, sources the sport, season, colours and the real owned crest, shows you every line it took and where it came from, and builds from there. No pickers, no uploads, no crest chosen by hand.',
    guarantees: [
      'You supply no logo — and the piece still carries the customer’s REAL owned crest, injected by the locked layer, never drawn.',
      'Every sourced line shows the record it came from, and you can correct any of them before anything renders.',
      'A field no record answers is left out, never guessed. A school with no owned crest on file blocks the build and says so.',
    ],
  },
  {
    id: LEVEL_BEZEL_DROP,
    n: 3,
    /* Never posted. See the note above LEVEL_BEZEL_DROP: the sheet comes back
     * through the GPT bridge, so there is no engine mode to satisfy. */
    mode: LEVEL_BEZEL_DROP,
    viaBridge: true,
    operatingMode: 'Directed Design',
    label: 'Bezel Drop',
    tagline: 'One mark, every housing, one sheet.',
    steps: false,
    /* ⚠️ THIS LEVEL DOES NOT MEET SHARED_GUARANTEES, AND SAYS SO RATHER THAN
     * PRETENDING TO. The guarantee that matters — "the client crest is the real
     * owned file, reproduced faithfully, never redrawn" — is a promise the
     * render engine's locked layer keeps. A Custom GPT has no locked layer: it
     * is shown the mark and DRAWS AN APPROXIMATION of it. That is acceptable
     * for a sales sheet and unacceptable for a production file, so the
     * departure is declared here, printed on screen, and written into the
     * provenance of the file that comes back (mark=drawn-by-model).
     *
     * ⚠️ AND THE PLATFORM DOES NOT STOP ANYONE SENDING IT. There is no gate in
     * this code that prevents a drawn-by-model sheet being emailed to a client.
     * Saying there is would be the more comfortable sentence and the false one. */
    sharedSpec: false,
    departs: 'The mark on the sheet is DRAWN BY THE MODEL from the file you supply — an approximation, not the placed file. Nothing in the platform prevents this sheet being sent, so that judgement is yours.',
    inputBurden: 'One mark — uploaded, or the team’s owned mark if one is on file.',
    what: 'Not a ring build. ONE client mark is held across the existing SR ring housings and comes back as a single branded sheet: landscape, pure black field, soft reflections, about nineteen housings in three rows, all straight on to the camera, the SR hexagon and line top left. It goes out through the GPT bridge, the same proven path the ring views use, and the sheet lands owned in HubSpot File Manager.',
    guarantees: [
      'Three ways to supply the mark, and UPLOAD IS FIRST — measured live 2026-09-14, 0 of 335 high schools carry an owned crest, so an owned-mark-only route is a dead end for most of the universe.',
      'The manual round trip is permanently on screen, not an error screen: copy the prompt, open the GPT, upload what it draws.',
      'The sheet lands owned in HubSpot File Manager, named with its provenance, and is recorded against the build when there is one.',
      'The sheet is labelled drawn-by-model wherever it appears. It is an approximation of the mark, never the placed file.',
    ],
  },
]

export const LEVEL_BY_ID = Object.fromEntries(LEVELS.map((l) => [l.id, l]))

/** The level a build's `mode` corresponds to. Mode stays the wire value. */
export function levelForMode(mode) {
  return LEVELS.find((l) => l.mode === mode) || LEVELS[0]
}

/** Does this level draw the 13-step spine, or the brief? */
export function levelUsesSteps(mode) {
  return levelForMode(mode).steps !== false
}

/**
 * The engine's own `modes` list is still the authority on what may be posted:
 * a level whose mode the backend does not accept must not be offered, or the
 * render 400s. Same rule as a style with no beast — it reads as unavailable
 * rather than as a dead control.
 */
export function levelRows(config) {
  const modes = (config && Array.isArray(config.modes) ? config.modes : []).map((m) => String(m.id || m))
  return LEVELS.map((l) => ({
    ...l,
    /* A bridge level never posts a mode, so the engine's list has no say over
     * it. Filtering it by `modes` would read as "coming" forever for a level
     * that works today. */
    available: l.viaBridge === true || modes.length === 0 || modes.includes(l.mode),
  }))
}

/** Does this level go out through the GPT bridge instead of the render engine? */
export function levelUsesBridge(mode) {
  return levelForMode(mode).viaBridge === true
}
